import React, { useEffect, useState } from "react";
import {
    CreditCard, ExternalLink, Loader2, Clock, FileText, Monitor, Layers3,
    CalendarDays, Clock3, ChartNoAxesColumnIncreasing, Zap, ReceiptText,
    WalletCards, ArrowUpCircle, CircleX, ChevronRight, Download, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Billing — subscription/usage/invoices console.
 *
 * Sidebar, header, verification banner (rendered globally by
 * PortalLayout), Stripe integration, subscription/licensing logic, and
 * device-activation logic are all untouched. This file only redesigns
 * the content area, reading real data from GET /billing/subscription
 * and the new GET /billing/invoices (backend/routers/billing_subscriptions.py).
 */

function daysUntil(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function formatMoney(cents, currency) {
    if (cents == null) return "—";
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "usd").toUpperCase() }).format(cents / 100);
    } catch {
        return `${(cents / 100).toFixed(2)} ${(currency || "").toUpperCase()}`;
    }
}

function TrialBanner({ status, endsAt }) {
    if (status !== "trialing") return null;
    const days = daysUntil(endsAt);
    return (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/[0.08] px-3 py-2 text-xs text-sky-600 dark:text-sky-300" data-testid="portal-trial-banner">
            <Clock size={13} className="shrink-0" />
            {days == null ? "You're on a trial." : days === 0
                ? "Your trial ends today. Add a payment method to keep using MoovBytes."
                : `Your trial ends in ${days} day${days === 1 ? "" : "s"}. Add a payment method to keep using MoovBytes without interruption.`}
        </div>
    );
}

const STATUS_META = {
    active: { label: "Active", dot: "bg-emerald-500", badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
    trialing: { label: "Trial", dot: "bg-sky-500", badge: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
    past_due: { label: "Past due", dot: "bg-amber-500", badge: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
    canceled: { label: "Cancelled", dot: "bg-[hsl(var(--destructive))]", badge: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]" },
};
function statusMeta(status) {
    return STATUS_META[status] || { label: (status || "Unknown").replace(/_/g, " "), dot: "bg-muted-foreground", badge: "bg-muted text-muted-foreground" };
}

const INVOICE_STATUS_META = {
    paid: { label: "Paid", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
    open: { label: "Open", cls: "bg-[#EEF4FF] text-brand dark:bg-brand/10" },
    past_due: { label: "Past due", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
    uncollectible: { label: "Failed", cls: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]" },
    void: { label: "Void", cls: "bg-muted text-muted-foreground" },
    draft: { label: "Draft", cls: "bg-muted text-muted-foreground" },
};

export default function PortalBilling() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";

    const [sub, setSub] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [invoices, setInvoices] = useState(null);
    const [invoicesErr, setInvoicesErr] = useState("");

    const loadSub = () => {
        setErr("");
        http.get("/billing/subscription").then(({ data }) => setSub(data)).catch((e) => setErr(errMsg(e)));
    };
    const loadInvoices = () => {
        setInvoicesErr("");
        http.get("/billing/invoices").then(({ data }) => setInvoices(data.invoices)).catch((e) => setInvoicesErr(errMsg(e)));
    };

    useEffect(() => { loadSub(); loadInvoices(); }, []);

    const openPortal = async () => {
        setBusy(true);
        try {
            const { data } = await http.post("/billing/portal", { return_url: window.location.href });
            window.location.href = data.url;
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    if (err) {
        return (
            <div className="p-6 sm:p-9">
                <div className="rounded-lg border border-border bg-surface p-6 text-sm text-muted-foreground" data-testid="portal-billing-error">
                    {err}
                </div>
            </div>
        );
    }
    if (!sub) {
        return (
            <div className="flex items-center justify-center p-16 text-muted-foreground" data-testid="portal-billing-page">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading billing…
            </div>
        );
    }

    const dev = sub.usage?.devices || {};
    const con = sub.usage?.concurrent || {};
    const retentionDays = sub.usage?.retention_days;
    const meta = statusMeta(sub.status);
    const renewalDate = sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "—";

    return (
        <div className="p-6 sm:p-9" data-testid="portal-billing-page">
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Subscription
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Billing</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Manage your subscription, usage, and billing details.</p>
                </div>
                {isAdmin && !sub.managed_externally && (
                    <button
                        onClick={openPortal}
                        disabled={busy}
                        data-testid="portal-billing-manage-btn"
                        aria-label="Manage subscription in Stripe"
                        className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                        <CreditCard size={14} /> {busy ? "Opening…" : "Manage in Stripe"} <ExternalLink size={12} />
                    </button>
                )}
            </div>

            <TrialBanner status={sub.status} endsAt={sub.current_period_end} />

            {sub.cancel_at_period_end && (
                <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/[0.08] px-3 py-2 text-xs text-amber-600 dark:text-amber-300">
                    Cancelled — access continues until {renewalDate}.
                </div>
            )}

            {/* Primary subscription summary card */}
            <div className="mb-4 grid grid-cols-1 overflow-hidden rounded-xl border border-border bg-surface lg:grid-cols-[1.15fr_1.2fr_0.9fr]" data-testid="portal-billing-summary">
                {/* Current plan */}
                <div className="border-b border-border p-5 lg:border-b-0 lg:border-r">
                    <div className="flex items-center gap-3.5">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] dark:bg-brand/10">
                            <FileText size={25} className="text-brand" />
                        </div>
                        <div>
                            <div className="text-xs font-medium text-muted-foreground">Current Plan</div>
                            <div className="mt-0.5 flex items-center gap-2">
                                <span className="font-display text-2xl font-bold tracking-tight">{sub.plan_label}</span>
                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold ${meta.badge}`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                                </span>
                            </div>
                        </div>
                    </div>
                    {sub.plan_description && (
                        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{sub.plan_description}</p>
                    )}
                </div>

                {/* Usage */}
                <div className="space-y-5 border-b border-border p-5 lg:border-b-0 lg:border-r">
                    <UsageRow icon={Monitor} label="Devices" used={dev.used} cap={dev.cap} pct={dev.pct} />
                    <UsageRow icon={Layers3} label="Concurrent migrations" used={con.used} cap={con.cap} pct={con.pct} />
                </div>

                {/* Subscription/billing metadata */}
                <div className="space-y-4 p-5">
                    <MetaRow icon={CalendarDays} label="Renewal date" value={sub.managed_externally ? "No renewal scheduled" : renewalDate} neutral />
                    <MetaRow icon={Clock3} label="Retention period" value={retentionDays == null ? "Unlimited" : `${retentionDays} days`} neutral />
                    <MetaRow icon={CreditCard} label="Billing status" value={meta.label} dot={meta.dot} neutral />
                </div>
            </div>

            {/* Secondary 3-card grid */}
            <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <PlanLimitsCard dev={dev} con={con} />
                <BillingDetailsCard sub={sub} renewalDate={renewalDate} retentionDays={retentionDays} meta={meta} />
                {isAdmin ? (
                    <QuickActionsCard sub={sub} busy={busy} onManage={openPortal} />
                ) : (
                    <div className="rounded-xl border border-border bg-surface p-4 text-xs text-muted-foreground">
                        Only organization admins can manage billing actions.
                    </div>
                )}
            </div>

            {/* Recent invoices */}
            <RecentInvoicesCard invoices={invoices} error={invoicesErr} onRetry={loadInvoices} />
        </div>
    );
}

function UsageRow({ icon: Icon, label, used = 0, cap, pct = 0 }) {
    const unlimited = cap == null;
    return (
        <div>
            <div className="flex items-start gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[#EEF4FF] dark:bg-brand/10">
                    <Icon size={16} className="text-brand" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-muted-foreground">{label}</span>
                        {!unlimited && <span className="text-muted-foreground">{pct}%</span>}
                    </div>
                    <div className="mt-0.5 font-display text-base font-bold tabular-nums">
                        {unlimited ? `${used} · Unlimited` : `${used} / ${cap}`}
                    </div>
                    {!unlimited && (
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                            <div
                                className={`h-full rounded-full ${pct >= 90 ? "bg-[hsl(var(--destructive))]" : pct >= 80 ? "bg-amber-500" : "bg-brand"}`}
                                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function MetaRow({ icon: Icon, label, value, dot }) {
    return (
        <div className="flex items-start gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-muted/60">
                <Icon size={16} className="text-muted-foreground" />
            </div>
            <div>
                <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold">
                    {dot && <span className={`h-2 w-2 rounded-full ${dot}`} />}
                    {value}
                </div>
            </div>
        </div>
    );
}

function SecondaryCard({ icon: Icon, title, children }) {
    return (
        <div className="rounded-xl border border-border bg-surface p-4 shadow-[0_1px_2px_rgba(16,24,40,0.02)]">
            <div className="mb-4 flex items-center gap-2">
                <div className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-[#EEF4FF] dark:bg-brand/10">
                    <Icon size={15} className="text-brand" />
                </div>
                <h2 className="font-display text-sm font-bold">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function PlanLimitsCard({ dev, con }) {
    return (
        <SecondaryCard icon={ChartNoAxesColumnIncreasing} title="Plan Limits">
            <div className="space-y-4">
                <LimitRow label="Devices" used={dev.used} cap={dev.cap} pct={dev.pct} />
                <LimitRow label="Concurrent migrations" used={con.used} cap={con.cap} pct={con.pct} />
            </div>
        </SecondaryCard>
    );
}

function LimitRow({ label, used = 0, cap, pct = 0 }) {
    const unlimited = cap == null;
    return (
        <div>
            <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
            <div className="mt-1 flex items-center justify-between">
                <span className="text-[15px] font-bold tabular-nums">{unlimited ? `${used} / Unlimited` : `${used} / ${cap}`}</span>
                {!unlimited && <span className="text-[11px] font-medium text-muted-foreground">{pct}%</span>}
            </div>
            {!unlimited && (
                <div className="mt-1.5 h-[7px] w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-brand" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
                </div>
            )}
        </div>
    );
}

function BillingDetailsCard({ sub, renewalDate, retentionDays, meta }) {
    const pm = sub.payment_method;
    const rows = [
        { label: "Customer name", value: sub.customer_name || "—" },
        { label: "Billing status", value: meta.label, dot: meta.dot },
        { label: "Renewal date", value: sub.managed_externally ? "No renewal scheduled" : renewalDate },
        { label: "Retention period", value: retentionDays == null ? "Unlimited" : `${retentionDays} days` },
        {
            label: "Payment method",
            value: pm ? `${pm.brand?.toUpperCase()} •••• ${pm.last4}` : sub.managed_externally ? "Managed contract" : "Manage in Stripe",
            link: !pm && !sub.managed_externally,
        },
    ];
    return (
        <SecondaryCard icon={CreditCard} title="Billing Details">
            <div>
                {rows.map((r, i) => (
                    <div
                        key={r.label}
                        className={`grid grid-cols-[42%_1fr] items-center py-2 text-xs ${i < rows.length - 1 ? "border-b border-border/60" : ""}`}
                    >
                        <span className="font-medium text-muted-foreground">{r.label}</span>
                        <span className={`flex items-center gap-1.5 font-semibold ${r.link ? "text-brand" : "text-foreground"}`}>
                            {r.dot && <span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} />}
                            {r.value}
                        </span>
                    </div>
                ))}
            </div>
        </SecondaryCard>
    );
}

function QuickActionsCard({ sub, busy, onManage }) {
    const actions = [
        !sub.managed_externally && { key: "manage", icon: CreditCard, label: busy ? "Opening…" : "Manage in Stripe", onClick: onManage, external: true },
        { key: "invoices", icon: FileText, label: "View invoices", onClick: () => document.querySelector('[data-testid="portal-billing-invoices"]')?.scrollIntoView({ behavior: "smooth" }) },
        !sub.managed_externally && { key: "payment", icon: WalletCards, label: "Update payment method", onClick: onManage, external: true },
        !sub.managed_externally && { key: "change", icon: ArrowUpCircle, label: "Change plan", onClick: onManage },
        !sub.managed_externally && { key: "cancel", icon: CircleX, label: "Cancel subscription", onClick: onManage },
    ].filter(Boolean);
    return (
        <SecondaryCard icon={Zap} title="Quick Actions">
            <div>
                {actions.map((a, i) => (
                    <button
                        key={a.key}
                        onClick={a.onClick}
                        disabled={busy}
                        aria-label={a.label}
                        className={`flex w-full items-center justify-between gap-2 py-2.5 text-left text-xs font-medium hover:bg-muted/40 disabled:opacity-50 ${i < actions.length - 1 ? "border-b border-border/60" : ""}`}
                    >
                        <span className="flex items-center gap-2">
                            <a.icon size={14} className="text-muted-foreground" /> {a.label}
                        </span>
                        {a.external ? <ExternalLink size={12} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                    </button>
                ))}
            </div>
        </SecondaryCard>
    );
}

function RecentInvoicesCard({ invoices, error, onRetry }) {
    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="portal-billing-invoices">
            <div className="flex items-center justify-between border-b border-border px-4 py-3.5">
                <div className="flex items-center gap-2">
                    <div className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-[#EEF4FF] dark:bg-brand/10">
                        <ReceiptText size={15} className="text-brand" />
                    </div>
                    <h2 className="font-display text-sm font-bold">Recent Invoices</h2>
                </div>
            </div>

            {error ? (
                <div className="flex flex-col items-center gap-3 p-8 text-center text-sm text-muted-foreground">
                    <span>Unable to load invoices. {error}</span>
                    <button onClick={onRetry} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted/40">
                        <RefreshCw size={12} /> Retry
                    </button>
                </div>
            ) : invoices === null ? (
                <div className="flex items-center justify-center p-12 text-muted-foreground">
                    <Loader2 size={16} className="mr-2 animate-spin" /> Loading invoices…
                </div>
            ) : invoices.length === 0 ? (
                <div className="flex flex-col items-center gap-1.5 p-10 text-center">
                    <ReceiptText size={28} className="mb-1 text-muted-foreground" />
                    <div className="text-sm font-semibold">No invoices yet</div>
                    <div className="text-xs text-muted-foreground">Your invoices will appear here once billing activity is available.</div>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                        <thead>
                            <tr className="bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                <th className="px-4 py-2">Invoice</th>
                                <th className="px-4 py-2">Date</th>
                                <th className="px-4 py-2">Amount</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => {
                                const st = INVOICE_STATUS_META[inv.status] || { label: inv.status, cls: "bg-muted text-muted-foreground" };
                                return (
                                    <tr key={inv.id} className="border-t border-border/60">
                                        <td className="px-4 py-2.5 font-semibold">{inv.number || inv.id}</td>
                                        <td className="px-4 py-2.5 text-muted-foreground">
                                            {inv.created ? new Date(inv.created * 1000).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="px-4 py-2.5 font-medium">
                                            {formatMoney(inv.amount_paid ?? inv.amount_due, inv.currency)}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center justify-end gap-1">
                                                {inv.hosted_invoice_url && (
                                                    <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" aria-label={`View invoice ${inv.number || inv.id}`} className="flex h-[30px] w-[30px] items-center justify-center rounded text-brand hover:bg-muted/60">
                                                        <ExternalLink size={14} />
                                                    </a>
                                                )}
                                                {inv.invoice_pdf && (
                                                    <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" aria-label={`Download invoice ${inv.number || inv.id} PDF`} className="flex h-[30px] w-[30px] items-center justify-center rounded text-brand hover:bg-muted/60">
                                                        <Download size={14} />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
